// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import type { Lang } from "@/hooks/useLang/types";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyBranchDetail } from "@/types";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import SlugInfoPopup from "@/UI/popups/SlugInfoPopup";
import { hasAnyLangValue, isValidSlug, readLangs, sanitizeSlugInput, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type FormBranchPopupDetails = {
    onSuccess: () => void;
    editBranchId?: string;
    pathPrefix?: string;
};

type Props = {
    onClose: () => void;
    details?: FormBranchPopupDetails;
};

type FormState = {
    name: TyLangValues;
    slug: string;
    address: TyLangValues;
    mapsUrl: string;
};

// --- Logic --------------------------------------------------------------------------------------

export default function FormBranchPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { languages, defaultLang } = useStoreLanguages();
    const { addComp, removeComp } = usePopup();

    const isEdit = Boolean(details?.editBranchId);

    const [form, setForm] = useState<FormState>({ name: {}, slug: "", address: {}, mapsUrl: "" });

    const setLangField = (key: "name" | "address", lang: Lang, value: string) =>
        setForm((prev) => ({ ...prev, [key]: { ...prev[key], [lang]: value } }));

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    /** Checked as it is typed, like the store slug — waiting for submit to reject it is needless. */
    const isSlugValid = isValidSlug(form.slug);

    // Fetch branch detail for edit mode
    const { data: detailData, loading: detailLoading } = useFetch<TyApiResponse<{ branch: TyBranchDetail }>>(
        `/branches/${details?.editBranchId ?? "x"}`,
        {},
        isEdit ? undefined : false
    );

    useEffect(() => {
        const branch = detailData?.data?.branch;
        if (!branch) return;
        setForm({
            name: readLangs(branch.name, languages),
            slug: branch.slug,
            address: readLangs(branch.address, languages),
            mapsUrl: branch.mapsUrl ?? "",
        });
    }, [detailData]);

    // Submit
    const prefix = details?.pathPrefix ?? "/branches";
    const submitPath = isEdit ? `${prefix}/${details!.editBranchId}` : prefix;
    const submitMethod = isEdit ? "PUT" : "POST";

    const {
        data: submitData,
        loading: submitting,
        error: submitError,
        invoke: submit,
    } = useFetch<TyApiResponse<unknown>>(submitPath, { method: submitMethod }, false);

    useEffect(() => {
        if (!submitData?.success) return;
        details?.onSuccess();
        onClose();
    }, [submitData]);

    /** Same jargon problem as the store slug, and the same permanence once branch codes are printed. */
    const openSlugInfo = () => {
        const name = "slug-info";
        removeComp(name);
        addComp({ name, Component: SlugInfoPopup, place: "center", anim: "scale", cover: "shaded", details: { variant: "branch" } });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const trimSlug = form.slug.trim();
        if (!hasAnyLangValue(form.name) || !isSlugValid) return;

        submit({
            data: {
                name: writeLangs(form.name, languages, defaultLang),
                slug: trimSlug,
                address: writeLangs(form.address, languages, defaultLang),
                mapsUrl: form.mapsUrl.trim() || null,
            },
        });
    };

    const isSubmitDisabled = submitting || detailLoading || !hasAnyLangValue(form.name) || !isSlugValid;

    return (
        <div className="form-branch-popup">
            <div className="form-branch-popup-header">
                <p className="form-branch-popup-title">{isEdit ? t.formBranch.editTitle : t.formBranch.createTitle}</p>
                <button type="button" className="form-branch-popup-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <form className="fbp-body" onSubmit={handleSubmit} noValidate>
                <MultiLangInput
                    label={t.formBranch.nameLabel}
                    languages={languages}
                    values={form.name}
                    onChange={(lang, value) => setLangField("name", lang, value)}
                    disabled={submitting}
                />

                <div className="fbp-field-group">
                    <div className="fbp-slug-field">
                        <input
                            className={`fbp-slug-input${form.slug && !isSlugValid ? " fbp-slug-input--error" : ""}`}
                            type="text"
                            dir="ltr"
                            value={form.slug}
                            onChange={(e) => setField("slug", sanitizeSlugInput(e.target.value))}
                            placeholder={t.formBranch.slugPlaceholder}
                            disabled={submitting || detailLoading || isEdit}
                        />
                        <button type="button" className="fbp-slug-info" onClick={openSlugInfo} aria-label={t.slugInfo.branchTitle}>
                            <img src="/icons/info-circle-icon.svg" alt="" width={18} height={18} />
                        </button>
                    </div>
                    {form.slug && !isSlugValid ? (
                        <p className="fbp-field-hint fbp-field-hint--error">{t.formBranch.slugHint}</p>
                    ) : (
                        <p className="fbp-field-hint">{t.formBranch.slugHint}</p>
                    )}
                </div>

                <MultiLangInput
                    label={t.formBranch.addressLabel}
                    languages={languages}
                    values={form.address}
                    onChange={(lang, value) => setLangField("address", lang, value)}
                    placeholders={t.formBranch.addressPlaceholders}
                    disabled={submitting}
                />

                <div className="fbp-field">
                    {/* Whatever the branch pasted, saved as-is — every map app shares a different URL
                        shape, and rewriting one is how a working link stops working. */}
                    <input
                        type="url"
                        dir="ltr"
                        className="fbp-input"
                        placeholder={t.formBranch.mapsUrlPlaceholder}
                        value={form.mapsUrl}
                        onChange={(e) => setForm((prev) => ({ ...prev, mapsUrl: e.target.value }))}
                        disabled={submitting}
                    />
                    <p className="fbp-field-hint">{t.formBranch.mapsUrlHint}</p>
                </div>

                {submitError && <p className="fbp-error">{submitError}</p>}

                <button type="submit" className="fbp-submit" disabled={isSubmitDisabled}>
                    {submitting ? <span className="fbp-spinner" /> : isEdit ? t.formBranch.submitEdit : t.formBranch.submit}
                </button>
            </form>
        </div>
    );
}
